import debounce from 'lodash/debounce';
import { ComponentType, FocusEvent, useEffect, useMemo, useRef, useState } from 'react';
import { InputText, InputTextProps } from 'ui/inputs/inputText';
import {
  BaseOption,
  BasicFilterSelect,
  BasicSelect,
  OnSelectHandler,
  SelectValueDisplayProps,
} from 'ui/selects';
import {
  ComboWrapper,
  GhostSelectWrapper,
  InputWrapper,
  PrefixMask,
  StyledSelectOptionsContainer,
} from './InputSelectCombo.styles';

export enum SelectAlign {
  LEFT = 'left',
  RIGHT = 'right',
}

export type InputSelectComboProps = {
  /* unique identifier for the component */
  id: string;
  /* value used for the input element */
  inputValue: string;
  /* value used for the select element */
  selectValue?: string;
  /* prefix used for rendering and auto prepending to input values on change */
  prefixString?: string;
  /* enables automatically stripping the prefix from paste changes to the input */
  autoStripPrefix?: boolean;
  /* select options to render and select in the dropdown */
  options?: BaseOption[];
  /* renders the select component at the start or end of the component */
  selectAlign?: SelectAlign;
  /* function invoked when the select value changes */
  onSelect: OnSelectHandler;
  /* function invoked when the input value changes */
  onChange: (value: string) => void;
  /* function invoked when the search value changes in the Select */
  onSearchChange?: (value?: string | undefined) => void;
  /* Component to override rendering of the Select trigger */
  SelectTrigger?: ComponentType<React.PropsWithChildren<SelectValueDisplayProps>>;
  /* whether the select should have filtering functionality */
  hasFilter?: boolean;
  /* InputText props are available to pass through to the internally used InputText component */
} & Omit<InputTextProps, 'value'>;

const INPUT_CLASS_NAME = 'inputGhostWrapper';

export const InputSelectCombo = ({
  id,
  'data-test': dataTest,
  selectValue,
  inputValue,
  prefixString = '',
  autoStripPrefix = false,
  options,
  selectAlign = SelectAlign.LEFT,
  onSelect,
  onChange,
  onSearchChange,
  hasError,
  isReadOnly,
  isDisabled,
  SelectTrigger,
  width,
  hasFilter = true,
  ...props
}: InputSelectComboProps) => {
  const [inputHasFocus, setInputHasFocus] = useState(false);
  const prefixRef = useRef(prefixString);
  // Since we're rendering the prefix in a separate uneditable div we need to strip it before putting the value in the input
  const renderedInputValue = useMemo(
    () =>
      !!prefixString && inputValue?.startsWith(prefixString)
        ? inputValue?.replace(prefixString, '')
        : inputValue,
    [inputValue, prefixString],
  );

  // If prefix changes we need to also replace the prefix in the input value to match
  useEffect(() => {
    if (!!prefixString && prefixRef.current !== prefixString) {
      const withNewPrefix = inputValue?.startsWith(prefixRef.current)
        ? inputValue?.replace(prefixRef.current, prefixString)
        : inputValue;
      onChange(withNewPrefix);
      prefixRef.current = prefixString;
    }
  }, [prefixString]);

  const handleInputChange = (value: string) => {
    onChange(
      !prefixString ||
        (!renderedInputValue &&
          value?.length > 1 &&
          value.startsWith(prefixString) &&
          autoStripPrefix)
        ? value
        : prefixString + value,
    );
  };

  const handleFocus = (e: FocusEvent<HTMLInputElement, Element>) => {
    props.onFocus?.(e);
    setInputHasFocus(true);
  };

  const handleBlur = (e: FocusEvent<HTMLInputElement, Element>) => {
    props.onBlur?.(e);
    setInputHasFocus(false);
  };

  return (
    <ComboWrapper
      id={id}
      data-test={dataTest ?? `input-select-combo-${id}`}
      data-select-align={selectAlign}
      data-has-error={hasError}
      data-read-only={isReadOnly || isDisabled}
      data-input-focused={inputHasFocus}
      width={width}
    >
      {options &&
        (hasFilter ? (
          <BasicFilterSelect
            data-test="basic-filter-select"
            id={`input-select-combo-select-${id}`}
            options={options}
            defaultValues={selectValue ? [selectValue] : []}
            onSelect={onSelect}
            SelectWrapper={GhostSelectWrapper}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            onSearchChange={onSearchChange && debounce(onSearchChange, 300)}
            Trigger={SelectTrigger}
            SelectOptionsContainer={StyledSelectOptionsContainer}
          />
        ) : (
          <BasicSelect
            data-test="basic-select"
            id={`input-select-combo-select-${id}`}
            options={options}
            defaultValues={selectValue ? [selectValue] : []}
            onSelect={onSelect}
            SelectWrapper={GhostSelectWrapper}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            Trigger={SelectTrigger}
            SelectOptionsContainer={StyledSelectOptionsContainer}
          />
        ))}
      <InputWrapper>
        {prefixString && <PrefixMask>{prefixString}</PrefixMask>}
        <InputText
          id={`input-select-combo-input-${id}`}
          width="100%"
          value={renderedInputValue}
          onChange={handleInputChange}
          className={INPUT_CLASS_NAME}
          isReadOnly={isReadOnly}
          isDisabled={isDisabled}
          {...props}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </InputWrapper>
    </ComboWrapper>
  );
};
